import { isNil } from 'lodash';
import { useRef } from 'react';
import { CardNavigationSubtableRow } from '@shared/scenes/cards/navigation';
import { getSubtableRowNumber } from './subtableColumns';

/** A row open in the menu, with its 1-based position for the title. */
export interface SubtableRow {
  entityId: string;
  /** Unknown while the row isn't among the loaded rows (e.g. a link to one past the first page). */
  rowNumber?: number;
}

export interface SubtableRowMenuState {
  isOpen: boolean;
  row: SubtableRow | null;
  selectedRowId: string | undefined;
  openNew: () => void;
  openRow: (entityId: string) => void;
  close: () => void;
}

/** Where one surface's row menu lives: the card form's subtable route, or a stand-in for it. */
export interface RowMenuRoute {
  /** The new row form or an existing row; unset while the menu is closed. */
  target: CardNavigationSubtableRow | undefined;
  open: (target: CardNavigationSubtableRow) => void;
  close: () => void;
  /** The loaded rows' ids, in order, for the open row's number. */
  rowIds: string[];
}

/**
 * One surface's row menu, purely from its route: open exactly while the route names a row or the
 * new row form, and opening or closing it only navigates. A navigation the row form blocks (unsaved
 * changes) so leaves the menu as it was. Closing keeps the last form, so the panel keeps showing it
 * while it animates out instead of flashing the new row form.
 */
export function useSubtableRowMenu({
  target,
  open,
  close,
  rowIds,
}: RowMenuRoute): SubtableRowMenuState {
  const isOpen = !isNil(target);
  const openRow = target?.type === 'edit' ? { entityId: target.rowId } : null;
  const lastShownRowRef = useRef<SubtableRow | null>(null);
  if (isOpen) {
    lastShownRowRef.current = openRow;
  }
  const shownRow = isOpen ? openRow : lastShownRowRef.current;
  const row = isNil(shownRow) ? null : withRowNumber(shownRow, rowIds);

  return {
    isOpen,
    /** The row the menu shows, kept through the close animation. */
    row,
    /** The row to highlight in the table: only while the menu is open. */
    selectedRowId: isOpen ? row?.entityId : undefined,
    openNew: () => open({ type: 'new' }),
    openRow: entityId => open({ type: 'edit', rowId: entityId }),
    close,
  };
}

/** The row's number comes from where it sits in the loaded rows, once they've loaded. */
function withRowNumber(row: SubtableRow, rowIds: string[]): SubtableRow {
  const index = rowIds.indexOf(row.entityId);
  if (index < 0) {
    return row;
  }
  return { ...row, rowNumber: getSubtableRowNumber(index) };
}
